import React from "react";
import { ActionIcon, Box, Card, Group, SimpleGrid, Text } from "@mantine/core";
import {
  IconBuildingFactory,
  IconChecklist,
  IconLayoutDashboard,
  IconUsers,
  type IconProps,
} from "@tabler/icons-react";
import { useDashboardMetrics } from "api/superAdmin/dashboardMatric";

interface DashboardMetricsResponse {
  totalProjects?: number;
  projectsGrowth?: number;
  totalOrganizations?: number;
  organizationsGrowth?: number;
  totalEmployees?: number;
  employeesGrowth?: number;
  completedTasks?: number;
  taskCompletionChange?: number;
}

interface Metric {
  label: string;
  value: number | string;
  delta?: string;
  icon: React.ComponentType<IconProps>;
  color: string;
}

interface FormatDeltaOptions {
  percent?: boolean;
  suffix?: string;
}

export default function CardAdmin(): JSX.Element {
  const { data } = useDashboardMetrics();
  const apiData = data as DashboardMetricsResponse | undefined;

  const formatDelta = (
    value: number | null | undefined,
    { percent = false, suffix = "this month" }: FormatDeltaOptions = {}
  ): string => {
    if (value === null || typeof value === "undefined") return "";

    let sign = "";
    if (value > 0) sign = "+";
    else if (value < 0) sign = "-";

    const abs = Math.abs(value);
    return `${sign}${abs}${percent ? "%" : ""} ${suffix}`.trim();
  };

  const metricData: Metric[] = [
    {
      label: "Total Projects",
      value: apiData?.totalProjects ?? 0,
      delta: formatDelta(apiData?.projectsGrowth, {
        percent: false,
        suffix: "this month",
      }),
      icon: IconLayoutDashboard,
      color: "blue",
    },
    {
      label: "Organizations",
      value: apiData?.totalOrganizations ?? 0,
      delta: formatDelta(apiData?.organizationsGrowth, {
        percent: false,
        suffix: "this month",
      }),
      icon: IconBuildingFactory,
      color: "purple",
    },
    {
      label: "Total Employees",
      value: apiData?.totalEmployees ?? 0,
      delta: formatDelta(apiData?.employeesGrowth, {
        percent: false,
        suffix: "this month",
      }),
      icon: IconUsers,
      color: "green",
    },
    {
      label: "Completed Tasks",
      value: apiData?.completedTasks ?? 0,
      delta: formatDelta(apiData?.taskCompletionChange, {
        percent: true,
        suffix: "vs last week",
      }),
      icon: IconChecklist,
      color: "orange",
    },
  ];

  return (
    <SimpleGrid
      cols={4}
      spacing="lg"
      mb={30}
      breakpoints={[{ maxWidth: "sm", cols: 1 }]}
    >
      {metricData.map((m) => (
        <Card key={m.label} shadow="sm" radius="md" p="lg">
          <Group position="apart">
            <Box>
              <Text weight={700} size="xl">
                {typeof m.value === "number"
                  ? m.value.toLocaleString()
                  : m.value}
              </Text>
              <Text size="sm" color="dimmed">
                {m.label}
              </Text>
              {m.delta ? (
                <Text
                  size="xs"
                  color={m.delta.trim().startsWith("-") ? "red" : "green"}
                  mt={4}
                >
                  {m.delta}
                </Text>
              ) : null}
            </Box>
            <ActionIcon size={40} radius="md" variant="filled" color={m.color}>
              <m.icon size={24} />
            </ActionIcon>
          </Group>
        </Card>
      ))}
    </SimpleGrid>
  );
}
